import { useEffect, useState } from "react";

import { fetchTenantAuthOptions } from "../../../api/admin/tenant/endpoints-referrers";
import {
    DEFAULT_PASSWORD_POLICY,
    SignInMethod,
    TenantAuthOptions
} from "../../interfaces/admin/tenant.interface";

const ALL_METHODS: Record<SignInMethod, boolean> = {
    password: true,
    google: true,
    microsoft: true,
    // no providers are known when the lookup failed, so no SSO buttons either
    sso: false
};

/**
 * Which sign-in methods the login page should offer for the tenant
 * (tenant_authentication_plan.md Phase 1). Display-only: the backend refuses
 * a disabled method on every request whatever the page shows. `null` while
 * loading so the page can hold back the form instead of flashing buttons
 * that then disappear; a failed lookup falls back to offering everything,
 * which is today's behavior.
 */
export const useTenantAuthOptions = (tenantOrigin?: string | null) => {
    const [options, setOptions] = useState<TenantAuthOptions | null>(null);

    useEffect(() => {
        let cancelled = false;
        fetchTenantAuthOptions(tenantOrigin || undefined)
            .then(response => {
                if (cancelled) return;
                const methods = response.data?.methods;
                setOptions({
                    methods: methods ? { ...ALL_METHODS, ...methods } : ALL_METHODS,
                    passwordPolicy: {
                        ...DEFAULT_PASSWORD_POLICY,
                        ...(response.data?.passwordPolicy ?? {})
                    },
                    ssoProviders: Array.isArray(response.data?.ssoProviders)
                        ? response.data.ssoProviders
                        : [],
                    selfRegistration: response.data?.selfRegistration === true
                });
            })
            .catch(() => {
                if (!cancelled)
                    setOptions({
                        methods: ALL_METHODS,
                        passwordPolicy: DEFAULT_PASSWORD_POLICY,
                        ssoProviders: [],
                        selfRegistration: false
                    });
            });
        return () => {
            cancelled = true;
        };
    }, [tenantOrigin]);

    return options;
};
